import type { Locale, Site } from "./index";

export const PLAN_IDS = ["free", "regular", "professional"] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export type PaidPlanId = Exclude<PlanId, "free">;

export const BILLING_PERIODS = ["monthly", "yearly"] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];

/**
 * Features are phrased as benefits: a plan either grants the feature or it
 * doesn't. Ordered as they should appear when comparing plans side by side.
 */
export const PLAN_FEATURES = [
  "unlimitedDrawings",
  "exportPdf",
  "ownLogo",
  "collaboration",
  "support247",
  "noWatermark",
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

export type Currency = "SEK" | "USD" | "EUR";

export interface Plan {
  planId: PlanId;
  /** Price in major units, e.g. 98 means 98 SEK */
  monthly: number;
  yearly: number;
  features: readonly PlanFeature[];
  /** Renders the "popular choice" badge */
  highlight?: boolean;
}

export interface SitePricing {
  currency: Currency;
  plans: readonly Plan[];
}

export const SITE_PRICING = {
  ritabygglov: {
    currency: "SEK",
    plans: [
      { planId: "free", monthly: 0, yearly: 0, features: [] },
      {
        planId: "regular",
        monthly: 98,
        yearly: 940,
        highlight: true,
        features: ["unlimitedDrawings", "exportPdf", "noWatermark"],
      },
      {
        planId: "professional",
        monthly: 298,
        yearly: 2860,
        features: [
          "unlimitedDrawings",
          "exportPdf",
          "ownLogo",
          "collaboration",
          "support247",
          "noWatermark",
        ],
      },
    ],
  },
  drawblueprint: {
    currency: "EUR",
    plans: [
      { planId: "free", monthly: 0, yearly: 0, features: [] },
      {
        planId: "regular",
        monthly: 9,
        yearly: 86,
        highlight: true,
        features: ["unlimitedDrawings", "exportPdf", "noWatermark"],
      },
      {
        planId: "professional",
        monthly: 27,
        yearly: 260,
        features: [
          "unlimitedDrawings",
          "exportPdf",
          "ownLogo",
          "collaboration",
          "support247",
          "noWatermark",
        ],
      },
    ],
  },
} as const satisfies Record<Site, SitePricing>;

/** The plan ids a given site offers, as literal types. */
export type SupportedPlanId<S extends Site> =
  (typeof SITE_PRICING)[S]["plans"][number]["planId"];

export type SupportedPaidPlanId<S extends Site> = Exclude<
  SupportedPlanId<S>,
  "free"
>;

export const getSitePricing = (site: Site): SitePricing => SITE_PRICING[site];

export const getPlan = (site: Site, planId: string): Plan | undefined =>
  SITE_PRICING[site].plans.find((plan) => plan.planId === planId);

export const isPaidPlanId = (value: unknown): value is PaidPlanId =>
  value === "regular" || value === "professional";

export const isBillingPeriod = (value: unknown): value is BillingPeriod =>
  BILLING_PERIODS.includes(value as BillingPeriod);

export const isPlanSupported = (site: Site, planId: string): boolean =>
  getPlan(site, planId) !== undefined;

/** Every feature offered by any of the site's plans, in PLAN_FEATURES order. */
export const getSiteFeatures = (site: Site): PlanFeature[] =>
  PLAN_FEATURES.filter((feature) =>
    getSitePricing(site).plans.some((plan) => plan.features.includes(feature))
  );

/** e.g. 98 SEK -> "98 kr" (sv), 12 USD -> "$12" (en) */
export const formatPrice = (
  amount: number,
  currency: Currency,
  locale: Locale
): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

/** What a yearly subscription costs per month, rounded down. */
export const monthlyEquivalent = (plan: Plan): number =>
  Math.floor(plan.yearly / 12);

/** Savings when paying yearly rather than monthly, as a whole percentage. */
export const yearlyDiscountPercent = (plan: Plan): number =>
  plan.monthly === 0
    ? 0
    : Math.round((1 - plan.yearly / (plan.monthly * 12)) * 100);

/**
 * The headline discount advertised on the billing period toggle, taken from
 * the site's most prominent paid plan.
 */
export const siteYearlyDiscountPercent = (site: Site): number => {
  const paidPlans = getSitePricing(site).plans.filter(
    (plan) => plan.planId !== "free"
  );
  const plan = paidPlans.find((p) => p.highlight) ?? paidPlans[0];
  return plan ? yearlyDiscountPercent(plan) : 0;
};
